import React, { useCallback, useEffect, useState } from "react";
import WebsiteConversionReport from "../websites/WebsiteConversionReport";
import WebsiteTopCampaigns from "../websites/WebsiteTopCampaigns";
import {
  syncInsightsTimeframeParamsToUrl,
  INSIGHTS_TIMEFRAME_CHANGE_EVENT,
} from "../../utils/insightsTimeframe";

const readTimeframeParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    timeframe: params.get("timeframe") || "last_30_days",
    startDate: params.get("start_date") || null,
    endDate: params.get("end_date") || null,
  };
};

const timeframeKey = ({ timeframe, startDate, endDate }) =>
  `${timeframe}|${startDate || ""}|${endDate || ""}`;

const AccountInsights = (props) => {
  const {
    account,
    websiteIds,
    reportsPath,
    insightsUrl,
    topCampaignsUrl,
    emptyStateUrl,
    timeframeTypes,
    timeframeOptions,
    conversionTimeframeLabel,
    startDate,
    endDate,
    currency,
    reportingGoals,
  } = props;

  const [timeframeParams, setTimeframeParams] = useState(readTimeframeParams);
  const [topCampaignsCache, setTopCampaignsCache] = useState({});
  const [topCampaignsRetryCount, setTopCampaignsRetryCount] = useState(0);
  const tfKey = timeframeKey(timeframeParams);

  useEffect(() => {
    const handlePopState = () => {
      setTimeframeParams(readTimeframeParams());
    };
    const handleTimeframeChange = () => {
      setTimeframeParams(readTimeframeParams());
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener(
      INSIGHTS_TIMEFRAME_CHANGE_EVENT,
      handleTimeframeChange
    );
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener(
        INSIGHTS_TIMEFRAME_CHANGE_EVENT,
        handleTimeframeChange
      );
    };
  }, []);

  useEffect(() => {
    if (
      timeframeParams.timeframe === "custom" &&
      (!timeframeParams.startDate || !timeframeParams.endDate)
    ) {
      return;
    }
    if (topCampaignsCache[tfKey]) return;

    setTopCampaignsCache((prev) => ({
      ...prev,
      [tfKey]: { status: "loading" },
    }));

    const url = new URL(topCampaignsUrl, window.location.origin);
    url.searchParams.set("view", "campaigns");
    url.searchParams.set("timeframe", timeframeParams.timeframe);
    if (timeframeParams.startDate) {
      url.searchParams.set("start_date", timeframeParams.startDate);
    }
    if (timeframeParams.endDate) {
      url.searchParams.set("end_date", timeframeParams.endDate);
    }

    let cancelled = false;
    fetch(url.toString(), { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setTopCampaignsCache((prev) => ({
          ...prev,
          [tfKey]: { status: "loaded", data: data.top_campaigns },
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setTopCampaignsCache((prev) => ({
          ...prev,
          [tfKey]: { status: "error" },
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [tfKey, topCampaignsUrl, topCampaignsRetryCount]);

  const refetchTopCampaigns = useCallback(() => {
    setTopCampaignsCache((prev) => {
      const next = { ...prev };
      delete next[tfKey];
      return next;
    });
    setTopCampaignsRetryCount((count) => count + 1);
  }, [tfKey]);

  const handleTopCampaignsTimeframeChange = useCallback(
    ({ timeframe, startDate: nextStartDate, endDate: nextEndDate }) => {
      syncInsightsTimeframeParamsToUrl(insightsUrl, {
        timeframe,
        startDate: nextStartDate,
        endDate: nextEndDate,
      });
    },
    [insightsUrl]
  );

  const topCampaignsEntry = topCampaignsCache[tfKey] || { status: "loading" };

  return (
    <div className="AccountInsights">
      <div className="col-md-12">
        <div className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space">
          <div className="col-xs-12">
            <WebsiteConversionReport
              website={{ id: `account-${account.id}` }}
              reportingGoals={reportingGoals}
              reportsPath={reportsPath}
              websiteIds={websiteIds}
              scopeLabel="Account-wide"
              title="Timeframe Performance"
              timeframeTypes={timeframeTypes}
              currency={currency}
              syncInsightsTimeframe={true}
              insightsUrl={insightsUrl}
              initialTimeframeLabel={conversionTimeframeLabel}
              initialStartDate={startDate}
              initialEndDate={endDate}
            />
          </div>
        </div>
      </div>

      <div className="col-md-12 mt-10">
        <div className="col-xs-12 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm some-vertical-space">
          <WebsiteTopCampaigns
            topCampaigns={topCampaignsEntry.data}
            reportingGoals={reportingGoals}
            showWebsiteNames={true}
            showSubmissions={true}
            timeframeOptions={timeframeOptions}
            selectedTimeframe={timeframeParams.timeframe}
            startDate={timeframeParams.startDate}
            endDate={timeframeParams.endDate}
            insightsUrl={insightsUrl}
            overview={false}
            hideSeeAll={true}
            emptyStateUrl={emptyStateUrl}
            currency={currency}
            onTimeframeChange={handleTopCampaignsTimeframeChange}
            loading={topCampaignsEntry.status === "loading"}
            loadError={topCampaignsEntry.status === "error"}
            onRetry={refetchTopCampaigns}
          />
        </div>
      </div>
    </div>
  );
};

export default AccountInsights;
